import {
  lazyPipe,
  validateApiRequest,
  validateApiResponse,
  withApiErrorCapture,
} from '@musinsa/apis-common-api-zod4';
import { executeStandardRequest } from '@musinsa/packages-http';
import urlJoin from 'url-join';
import z from 'zod';
import { privateHttpClient } from '../httpClient';
import { createApiResponseSchema } from '../utils';
import { campaignsApiPath } from './apiPath';

// BE 정식값은 TWENTY_NINE_CM (curator-api SeedingRecruitmentCampaign.platform). 과거 응답/모킹
// 호환 위해 29CM 도 수용하고 29CM 로 정규화한다. (TWENTY_NINE_CM 미수용 시 상세 조회 zod throw → 접근 불가)
const PlatformSchema = z
  .enum(['MUSINSA', '29CM', 'TWENTY_NINE_CM'])
  .transform((value) => (value === 'TWENTY_NINE_CM' ? '29CM' : value))
  .describe('Platform');

const CampaignStatusSchema = z.enum(['ACTIVE', 'INACTIVE']).describe('CampaignStatus');

const SnsChannelSchema = z
  .enum(['INSTAGRAM', 'YOUTUBE', 'TWITTER', 'TIKTOK', 'NAVER_BLOG', 'THREADS', 'SNAP'])
  .describe('SnsChannel');

const SeedingContentTypeSchema = z
  .enum(['IMAGE', 'SHORT_FORM', 'LONG_FORM', 'TEXT', 'BLOG_POST'])
  .describe('SeedingContentType');

const ContentTypeOptionSchema = z
  .object({
    type: SeedingContentTypeSchema,
    uploadDeadlineDays: z.number().int(),
  })
  .describe('ContentTypeOption');

const SeedingCampaignSchema = z
  .object({
    id: z.number().int(),
    platform: PlatformSchema,
    brandId: z.string(),
    // 표시용 브랜드명(예: '032씨'). brandId 는 코드('032c'). 응답엔 있으나 문서 미선언이라 nullish.
    brandName: z.string().nullish(),
    title: z.string(),
    status: CampaignStatusSchema,
    snsChannels: z.array(SnsChannelSchema),
    contentTypes: z.array(ContentTypeOptionSchema),
    // 각 항목에 '#' 접두어가 이미 포함됨 (예: '#광고'). 클라이언트에서 '#' 추가 금지 — 표시 시 공백 join.
    requiredHashtags: z
      .array(z.string())
      .describe("requiredHashtags — 각 항목에 '#' 접두어 포함 (예: '#광고')"),
    requestNote: z.string().nullable(),
    maxParticipants: z.number().int(),
    recruitmentStartAt: z.string(),
    recruitmentEndAt: z.string(),
    announcementAt: z.string(),
  })
  .describe('SeedingCampaign');

const SeedingGoodsItemSchema = z
  .object({
    id: z.number().int(),
    goodsNo: z.number().int(),
    slotNo: z.number().int(),
    category: z.string(),
    brandName: z.string(),
    goodsName: z.string(),
    imageUrl: z.string(),
    // 상품 PDP 랜딩 URL — 썸네일 클릭 시 이동 (완성된 절대 URL). (QAT-39446 / 0617 계약)
    webUrl: z.string(),
    price: z.number().int(),
    soldOut: z.boolean(),
    // 큐레이터가 이 goods 를 선택했는지(옵션 없는 단품도 선택 시 true). 신청/수락 전이면 false. (QAT-39515)
    selected: z.boolean(),
    // 신청/수락 시 선택한 옵션 표시 문자열(예: '라이트 베이지 · L'). 미선택·단품이면 null. (QAT-39515)
    selectedOption: z.string().nullish(),
  })
  .describe('SeedingGoodsItem');

const ParticipationStatusSchema = z
  .enum([
    'PENDING',
    'CONFIRMED',
    'NOT_SELECTED',
    'SELF_WITHDRAWN',
    'EXPIRED',
    'COMPLETED',
    'REJECTED',
    'AUTO_TIMEOUT',
  ])
  .describe('ParticipationStatus');

const ParticipationTypeSchema = z.enum(['RECRUITMENT']).describe('ParticipationType');

const SeedingParticipationSchema = z
  .object({
    id: z.number().int(),
    status: ParticipationStatusSchema,
    participationType: ParticipationTypeSchema,
    selectedContentType: SeedingContentTypeSchema.nullable(),
    messageToBrand: z.string().nullable(),
    contentSubmissionDeadlineAt: z.string().nullable(),
    confirmedAt: z.string().nullable(),
    endedAt: z.string().nullable(),
    rejectionReasons: z.array(z.string()).nullable(),
  })
  .describe('SeedingParticipation');

const FulfillmentStatusSchema = z
  .enum(['SHIPPING_READY', 'SHIPPING', 'DELIVERED', 'CANCELLED'])
  .describe('FulfillmentStatus');

const SeedingFulfillmentSchema = z
  .object({
    id: z.number().int(),
    orderId: z.string(),
    orderOptionId: z.number().int(),
    status: FulfillmentStatusSchema,
    shippingDeadlineAt: z.string(),
    shippedAt: z.string().nullable(),
    deliveredAt: z.string().nullable(),
    // 택배사명/송장번호/배송추적URL — 송장 등록 시 노출(배송준비중 포함), 미등록·취소·미지원 시 null. (API 0610 SeedingDetailFulfillment)
    courierName: z.string().nullish(),
    invoiceNo: z.string().nullish(),
    trackingUrl: z.string().nullish(),
  })
  .describe('SeedingFulfillment');

const SeedingContentSubmissionSchema = z
  .object({
    id: z.number().int(),
    channel: SnsChannelSchema,
    contentType: SeedingContentTypeSchema,
    contentUrl: z.string(),
  })
  .describe('SeedingContentSubmission');

const DeliveryAddressSourceSchema = z
  .enum(['DEFAULT', 'ORDER_SNAPSHOT'])
  .describe('DeliveryAddressSource');

const SeedingDeliveryAddressSchema = z
  .object({
    source: DeliveryAddressSourceSchema,
    // 배송지 별칭('우리집' 등). ORDER_SNAPSHOT 만 보유 / DEFAULT 는 null
    addressName: z.string().nullable(),
    name: z.string(),
    phone: z.string(),
    address1: z.string(),
    address2: z.string().nullable(),
    // 0610 — 신청·수락 payload 라운드트립용(우편번호 필수). 누락 시 zod 가 strip 해
    // payload zipcode 가 빈 문자열로 나가 BE '우편번호 필수' 에러가 나던 문제. (SID-4048)
    deliveryAddressId: z.number().int().nullable().optional(),
    zipcode: z.string().nullable().optional(),
  })
  .describe('SeedingDeliveryAddress');

const SeedingPenaltySchema = z
  .object({
    // 응답에 포함되면 항상 활성 패널티로 간주 (active 플래그 없음)
    reason: z.string(),
  })
  .describe('SeedingPenalty');

const GetSeedingRecruitmentDetailRequestSchema = z
  .object({
    campaignId: z.number().int(),
  })
  .describe('GetSeedingRecruitmentDetailRequest');

export type GetSeedingRecruitmentDetailRequest = z.infer<
  typeof GetSeedingRecruitmentDetailRequestSchema
>;

const GetSeedingRecruitmentDetailResponseDataSchema = z
  .object({
    campaign: SeedingCampaignSchema,
    goods: z.array(SeedingGoodsItemSchema),
    participation: SeedingParticipationSchema.nullable(),
    fulfillments: z.array(SeedingFulfillmentSchema),
    contentSubmissions: z.array(SeedingContentSubmissionSchema),
    deliveryAddress: SeedingDeliveryAddressSchema.nullable(),
    penalty: SeedingPenaltySchema.nullable(),
    // 발송 기한 영업일 수 N ("선정일로부터 영업일 N일 이내") — BE 정책 상수. (API 0615)
    shippingDeadlineDays: z.number().int().nullish(),
    // 예상 발송 마감일 (KST). 모집형=선정 발표일+N영업일. 공휴일 조회 실패 시 null. (API 0615)
    estimatedShippingDeadlineAt: z.string().nullish(),
  })
  .describe('GetSeedingRecruitmentDetailResponseData');

export type GetSeedingRecruitmentDetailResponseData = z.infer<
  typeof GetSeedingRecruitmentDetailResponseDataSchema
>;

const GetSeedingRecruitmentDetailResponseSchema = createApiResponseSchema(
  GetSeedingRecruitmentDetailResponseDataSchema,
);

export type GetSeedingRecruitmentDetailResponse = z.infer<
  typeof GetSeedingRecruitmentDetailResponseSchema
>;

export const getSeedingRecruitmentDetail = withApiErrorCapture(
  lazyPipe(
    validateApiRequest(GetSeedingRecruitmentDetailRequestSchema),
    async ({
      campaignId,
    }: GetSeedingRecruitmentDetailRequest): Promise<GetSeedingRecruitmentDetailResponse> => {
      return executeStandardRequest(async () =>
        privateHttpClient.get<GetSeedingRecruitmentDetailResponse>(
          urlJoin(campaignsApiPath.getSeedingRecruitmentDetail(campaignId)),
        ),
      );
    },
    validateApiResponse(GetSeedingRecruitmentDetailResponseSchema),
  ),
);
